import { DEFAULT_SCENE_CHARACTER_SCALE, type CharacterScale } from './avatarScale';
import { modernOfficeAnnotations, sunnySideAnnotations } from './worldCatalogAnnotations';
import { DEFAULT_CAMERA_ZOOM, DEFAULT_WORLD_LAYOUT, type WorldLayout } from './worldLayout';
import type { AnimatedGifPlacement } from '../types/animatedGif';
import type { WorldAnnotation } from '../types/worldAnnotation';
import type { ZoneBounds } from './worldConstants';

export type WorldId = string;

export interface TileRect {
  minTileX: number;
  maxTileX: number;
  minTileY: number;
  maxTileY: number;
}

export interface WorldOption {
  id: WorldId;
  name: string;
  description: string;
  previewPath: string;
}

interface PixelWalkableMask {
  type: 'pixel-sample';
  sourceTexture?: {
    key: string;
    path: string;
  };
  isBlockedColor: (red: number, green: number, blue: number, alpha: number) => boolean;
}

interface RectangleWalkableMask {
  type: 'blocked-rectangles';
  blockedAreas: TileRect[];
}

export type WorldWalkableMask = PixelWalkableMask | RectangleWalkableMask;

export interface NpcScatterLayout {
  spawnX: number;
  spawnY: number;
  waypointPath: Array<{ x: number; y: number }>;
}

export interface WorldProfile {
  id: WorldId;
  option: WorldOption;
  worldLayout: WorldLayout;
  defaultCameraZoom: number;
  backdrop: {
    key: string;
    path: string;
  };
  foreground?: {
    key: string;
    path: string;
  } | null;
  animatedGifs?: AnimatedGifPlacement[];
  collisionTileset: {
    key: string;
    path?: string;
  };
  playerSpawnTile: { x: number; y: number };
  defaultZone: string;
  zones: ZoneBounds[];
  annotations: WorldAnnotation[];
  ambientZoneProfile: Record<string, { frequency: number; gain: number }>;
  npcScatterLayout: NpcScatterLayout[];
  walkableMask: WorldWalkableMask;
  walkableCarveOuts?: TileRect[];
  characterScale: CharacterScale;
}

const sunnySideNpcScatterLayout: NpcScatterLayout[] = [
  {
    // Close to the player spawn.
    spawnX: 35,
    spawnY: 43,
    waypointPath: [
      { x: 35, y: 43 },
      { x: 37, y: 43 },
      { x: 37, y: 45 },
      { x: 35, y: 45 },
    ],
  },
  {
    // Second nearby NPC.
    spawnX: 30,
    spawnY: 41,
    waypointPath: [
      { x: 30, y: 41 },
      { x: 32, y: 41 },
      { x: 32, y: 43 },
      { x: 30, y: 43 },
    ],
  },
  {
    // North-west district.
    spawnX: 20,
    spawnY: 19,
    waypointPath: [
      { x: 20, y: 19 },
      { x: 22, y: 19 },
      { x: 22, y: 21 },
      { x: 20, y: 21 },
    ],
  },
  {
    // East path.
    spawnX: 49,
    spawnY: 25,
    waypointPath: [
      { x: 49, y: 25 },
      { x: 52, y: 25 },
      { x: 52, y: 27 },
      { x: 49, y: 27 },
    ],
  },
  {
    // South-east district.
    spawnX: 53,
    spawnY: 50,
    waypointPath: [
      { x: 53, y: 50 },
      { x: 56, y: 50 },
      { x: 56, y: 52 },
      { x: 53, y: 52 },
    ],
  },
];

const sunnySideProfile: WorldProfile = {
  id: 'sunny-side',
  worldLayout: { ...DEFAULT_WORLD_LAYOUT },
  defaultCameraZoom: DEFAULT_CAMERA_ZOOM,
  option: {
    id: 'sunny-side',
    name: 'Sunny Side',
    description: 'Open-air village paths, lakeside edges, and a bright social plaza.',
    previewPath: '/assets/backgrounds/sunnyside/village-forest-lakes-final.png',
  },
  backdrop: {
    key: 'sunnyside-village-backdrop',
    path: '/assets/backgrounds/sunnyside/village-forest-lakes-final.png',
  },
  collisionTileset: {
    key: 'sunnyside-world-tileset',
  },
  playerSpawnTile: { x: 33, y: 43 },
  defaultZone: 'village',
  zones: [
    { id: 'village', minTileX: 1, maxTileX: 26, minTileY: 1, maxTileY: 30 },
    { id: 'forest', minTileX: 34, maxTileX: 62, minTileY: 1, maxTileY: 30 },
    { id: 'library', minTileX: 18, maxTileX: 46, minTileY: 34, maxTileY: 62 },
  ],
  annotations: sunnySideAnnotations,
  ambientZoneProfile: {
    village: { frequency: 130, gain: 0.004 },
    forest: { frequency: 98, gain: 0.006 },
    library: { frequency: 156, gain: 0.0035 },
    wilderness: { frequency: 110, gain: 0.0025 },
  },
  npcScatterLayout: sunnySideNpcScatterLayout,
  walkableMask: {
    type: 'pixel-sample',
    sourceTexture: {
      key: 'sunnyside-traversal-mask',
      path: '/assets/backgrounds/sunnyside/village-forest-lakes-traversal.png',
    },
    isBlockedColor: (red, green, blue, alpha): boolean => {
      if (alpha < 10) {
        return true;
      }
      return red < 128 && green < 128 && blue < 128;
    },
  },
  characterScale: { ...DEFAULT_SCENE_CHARACTER_SCALE },
  walkableCarveOuts: [],
};

const modernOfficeProfile: WorldProfile = {
  id: 'office',
  worldLayout: { ...DEFAULT_WORLD_LAYOUT },
  defaultCameraZoom: DEFAULT_CAMERA_ZOOM,
  option: {
    id: 'office',
    name: 'Office',
    description: 'An interior floor with reception desks, pods, and collaborative work areas.',
    previewPath: '/assets/backgrounds/office/modern-office-design-final.png',
  },
  backdrop: {
    key: 'modern-office-backdrop',
    path: '/assets/backgrounds/office/modern-office-design-final.png',
  },
  collisionTileset: {
    key: 'modern-office-tileset',
    path: '/assets/tilesets/modern-office-16x16.png',
  },
  playerSpawnTile: { x: 31, y: 33 },
  defaultZone: 'operations',
  zones: [
    { id: 'north-wing', minTileX: 2, maxTileX: 61, minTileY: 2, maxTileY: 21 },
    { id: 'operations', minTileX: 2, maxTileX: 61, minTileY: 22, maxTileY: 37 },
    { id: 'focus-pods', minTileX: 2, maxTileX: 61, minTileY: 38, maxTileY: 50 },
    { id: 'support-desk', minTileX: 2, maxTileX: 61, minTileY: 51, maxTileY: 61 },
  ],
  annotations: modernOfficeAnnotations,
  ambientZoneProfile: {
    'north-wing': { frequency: 128, gain: 0.0032 },
    operations: { frequency: 116, gain: 0.0028 },
    'focus-pods': { frequency: 102, gain: 0.0034 },
    'support-desk': { frequency: 148, gain: 0.0029 },
  },
  npcScatterLayout: [
    {
      spawnX: 30,
      spawnY: 10,
      waypointPath: [
        { x: 27, y: 10 },
        { x: 34, y: 10 },
        { x: 34, y: 14 },
        { x: 27, y: 14 },
      ],
    },
    {
      spawnX: 15,
      spawnY: 28,
      waypointPath: [
        { x: 12, y: 28 },
        { x: 19, y: 28 },
        { x: 19, y: 34 },
        { x: 12, y: 34 },
      ],
    },
    {
      spawnX: 47,
      spawnY: 28,
      waypointPath: [
        { x: 44, y: 28 },
        { x: 52, y: 28 },
        { x: 52, y: 34 },
        { x: 44, y: 34 },
      ],
    },
    {
      spawnX: 16,
      spawnY: 44,
      waypointPath: [
        { x: 12, y: 44 },
        { x: 21, y: 44 },
        { x: 21, y: 50 },
        { x: 12, y: 50 },
      ],
    },
    {
      spawnX: 47,
      spawnY: 44,
      waypointPath: [
        { x: 43, y: 44 },
        { x: 52, y: 44 },
        { x: 52, y: 50 },
        { x: 43, y: 50 },
      ],
    },
  ],
  walkableMask: {
    type: 'pixel-sample',
    sourceTexture: {
      key: 'modern-office-traversal-mask',
      path: '/assets/backgrounds/office/modern-office-design-traversal.png',
    },
    isBlockedColor: (red, green, blue, alpha): boolean => {
      if (alpha < 10) {
        return true;
      }
      return red < 128 && green < 128 && blue < 128;
    },
  },
  walkableCarveOuts: [],
  characterScale: {
    npc: 2.8,
    playerFallback: 2.8,
    playerAvatar: 1.25,
    remoteNpc: 2.8,
    remoteAvatar: 1.25,
  },
};

export const DEFAULT_WORLD_ID: WorldId = 'sunny-side';

const worldProfileById: Record<string, WorldProfile> = {
  'sunny-side': sunnySideProfile,
  office: modernOfficeProfile,
};

export const WORLD_OPTIONS: WorldOption[] = Object.values(worldProfileById).map(
  (profile) => profile.option,
);

export const getWorldProfile = (worldId: WorldId): WorldProfile =>
  worldProfileById[worldId] ?? sunnySideProfile;
